/**
 * Product page accordion (Use & Care, Shipping & Return, Warranty):
 * smooth open/close, and opening one row closes the others.
 * Scoped to the product details so other accordions (filters, menus) keep their default behaviour.
 */
(() => {
  const SUMMARY_SELECTOR = '.product-details .accordion details > summary';
  const DURATION = 320;
  const EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Height of a row when only the summary is showing (borders included). */
  const getClosedHeight = (details) => {
    const summary = details.querySelector(':scope > summary');
    const borders = details.offsetHeight - details.clientHeight;
    return summary.offsetHeight + borders;
  };

  const toggle = (details, shouldOpen) => {
    const content = details.querySelector(':scope > .details-content');

    // Start from the current visual height, even if another animation is running.
    const from = details.getBoundingClientRect().height;
    details.getAnimations().forEach((animation) => animation.cancel());
    content?.getAnimations().forEach((animation) => animation.cancel());

    if (reducedMotion.matches) {
      details.open = shouldOpen;
      details.classList.remove('is-closing');
      return;
    }

    if (shouldOpen) {
      details.classList.remove('is-closing');
      details.open = true;
    }

    const to = shouldOpen ? details.getBoundingClientRect().height : getClosedHeight(details);
    if (!shouldOpen) details.classList.add('is-closing');

    details.style.overflow = 'hidden';
    // fill: forwards keeps the end height on screen until the row is really closed, so it never
    // jumps back to its open height for a frame when the animation ends (seen in Safari / Firefox).
    const animation = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: DURATION, easing: EASING, fill: 'forwards' }
    );
    content?.animate(
      { opacity: shouldOpen ? [0, 1] : [1, 0] },
      { duration: shouldOpen ? DURATION : DURATION * 0.6, easing: 'ease', fill: 'forwards' }
    );

    animation.onfinish = () => {
      if (!shouldOpen) {
        details.open = false;
        details.classList.remove('is-closing');
      }
      details.style.overflow = '';
      // Drop the held end state now that the open attribute matches it
      animation.cancel();
      content?.getAnimations().forEach((a) => a.cancel());
    };
  };

  document.addEventListener('click', (event) => {
    const summary = /** @type {Element} */ (event.target).closest(SUMMARY_SELECTOR);
    if (!summary) return;

    event.preventDefault();

    const details = /** @type {HTMLDetailsElement} */ (summary.parentElement);
    const isOpening = !details.open || details.classList.contains('is-closing');

    if (isOpening) {
      details
        .closest('.accordion')
        ?.querySelectorAll('details[open]')
        .forEach((other) => {
          if (other !== details && !other.classList.contains('is-closing')) toggle(other, false);
        });
    }

    toggle(details, isOpening);
  });
})();
